import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../../../index';
import { Player } from '../types';
import { sanitizeDisplayName } from '../utils/normalize';

interface RankedPlayer extends Player {
  rank: number;
}

export const IndividualRanking: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [topPlayers, setTopPlayers] = useState<RankedPlayer[]>([]);

  useEffect(() => {
    if (!sessionId) return;

    // すべてのプレイヤーを取得して、クライアント側でソート
    const unsubscribe = onSnapshot(
      collection(db, 'battleRoyale', sessionId, 'players'),
      (snapshot) => {
        const players = snapshot.docs.map((doc) => {
          const data = doc.data();
          // answeredIdsが存在する場合はその長さを、なければscoreを使用
          const calculatedScore = data.answeredIds ? data.answeredIds.length : (data.score || 0);
          return {
            id: doc.id,
            ...data,
            score: calculatedScore
          } as Player;
        });
        
        // スコアでソート（同点の場合は lastCorrectAt で早い順）
        players.sort((a, b) => {
          if (b.score !== a.score) {
            return b.score - a.score;
          }
          // lastCorrectAt で比較（早い方が上位）
          const aTime = a.lastCorrectAt ? 
            (typeof (a.lastCorrectAt as any).toDate === 'function' ? 
              (a.lastCorrectAt as any).toDate().getTime() : 
              new Date(a.lastCorrectAt as any).getTime()) : 0;
          const bTime = b.lastCorrectAt ? 
            (typeof (b.lastCorrectAt as any).toDate === 'function' ? 
              (b.lastCorrectAt as any).toDate().getTime() : 
              new Date(b.lastCorrectAt as any).getTime()) : 0;
          return aTime - bTime;
        });
        
        // 上位3名を取得してランクを付与
        const rankedPlayers = players.slice(0, 3).map((player, index) => ({
          ...player,
          rank: index + 1
        } as RankedPlayer));
        
        setTopPlayers(rankedPlayers);
      }
    );

    return () => unsubscribe();
  }, [sessionId]);

  const getRankSuffix = (rank: number) => {
    switch (rank) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        bgcolor: '#365964',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.2) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.2) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          pointerEvents: 'none'
        }
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          left: 40,
          color: 'white'
        }}
      >
        <Typography variant="h6">
          IndividualRanking {new Date().toLocaleDateString('ja-JP')}
        </Typography>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: 20,
          right: 40,
          color: 'white'
        }}
      >
        <Typography variant="h6">
          NAZOTOKI KOSHOKAI
        </Typography>
      </Box>

      {/* メインコンテンツ */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 16
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 1600 }}>
          {[1, 2, 3].map((rank) => {
            const player = topPlayers.find(p => p.rank === rank);
            return (
              <Box
                key={rank}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 3,
                  mb: 9
                }}
              >
                <Box sx={{ 
                  minWidth: 320,
                  display: 'flex',
                  alignItems: 'baseline'
                }}>
                  <Typography
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '8rem',
                      lineHeight: 1
                    }}
                  >
                    {rank}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '4.5rem',
                      lineHeight: 1,
                      ml: 0.5
                    }}
                  >
                    {getRankSuffix(rank)}
                  </Typography>
                </Box>
                
                <Typography
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '8rem',
                    lineHeight: 1,
                    flex: 1
                  }}
                >
                  {player ? sanitizeDisplayName(player.displayName) : '---'}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* フッター */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          left: 40,
          color: 'white'
        }}
      >
        <Typography variant="body1">
          NAZOTOKI BATTLE ROYAL
        </Typography>
      </Box>

      {/* 装飾的な要素 */}
      <Box
        sx={{
          position: 'absolute',
          top: 60,
          left: 60,
          width: 40,
          height: 40,
          border: '2px solid white',
          borderBottom: 'none',
          borderRight: 'none'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 60,
          right: 60,
          width: 40,
          height: 40,
          border: '2px solid white',
          borderBottom: 'none',
          borderLeft: 'none'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 60,
          left: 60,
          width: 40,
          height: 40,
          border: '2px solid white',
          borderTop: 'none',
          borderRight: 'none'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 60,
          right: 60,
          width: 40,
          height: 40,
          border: '2px solid white',
          borderTop: 'none',
          borderLeft: 'none'
        }}
      />
    </Box>
  );
};